<!-- 用户信息 -->
<template>
  <!-- 应用 -->
  <ele-dropdown
    style="margin-right: 10px"
    :items="switchAppConfigs"
    :icon-props="{ size: 15 }"
    :popper-options="{
      modifiers: [{ name: 'offset', options: { offset: [0, 5] } }]
    }"
    @command="onAppConfigDropClick"
  >
    <div class="header-avatar">
      <el-icon :size="13" style="margin-right: 4px">
        <AppstoreOutlined />
      </el-icon>
      <div style="margin-left: 4px">
        {{ currAppConfig?.appName }}
      </div>
    </div>
  </ele-dropdown>

  <el-divider direction="vertical" />
  <!-- 学年 -->
  <div v-if="switchDataScopes.length > 0">
    <ele-dropdown
      style="margin-right: 10px"
      :items="switchDataScopes"
      :icon-props="{ size: 15 }"
      :popper-options="{
        modifiers: [{ name: 'offset', options: { offset: [0, 5] } }]
      }"
      @command="onDataScopeDropClick"
    >
      <div class="header-avatar">
        <el-icon :size="13" style="margin-right: 4px">
          <ArrowRight />
        </el-icon>
        <div style="margin-left: 4px">
          {{ currDataScope?.dataScopeId }}
        </div>
      </div>
    </ele-dropdown>
  </div>
  <div v-else>
    <div class="header-avatar">
      <div style="margin-left: 4px">
        {{ currDataScope?.dataScopeId ?? '无数据' }}
      </div>
    </div>
  </div>

  <!-- 角色 -->
  <el-divider direction="vertical" />
  <div v-if="switchRoles.length > 0">
    <ele-dropdown
      ref="roleDropdownRef"
      :items="switchRoles"
      :icon-props="{ size: 15 }"
      :popper-options="{
        modifiers: [{ name: 'offset', options: { offset: [0, 5] } }]
      }"
      @command="onRoleDropClick"
    >
      <div class="header-avatar">
        <el-icon :size="13" style="margin-right: 4px">
          <ArrowRight />
        </el-icon>
        <div style="margin-left: 4px">
          {{ currRole.roleName ?? '无角色' }}
        </div>
      </div>
    </ele-dropdown>
  </div>
  <div v-else>
    <div class="header-avatar">
      <div style="margin-left: 4px">
        {{ currRole.roleName ?? '无角色' }}
      </div>
    </div>
  </div>

  <el-tour
    v-if="switchRoles.length > 0"
    v-model="open"
    :content-style="{ width: '300px' }"
  >
    <el-tour-step :target="roleDropdownRef?.$el">
      <template #default>
        <ele-text type="warning" :strong="true">点击下拉切换角色</ele-text>
      </template>
      <template #header>
        <strong style="font-size: 18px">角色切换提示</strong>
      </template>
    </el-tour-step>
    <template #indicators></template>
  </el-tour>
  <!-- 修改密码弹窗 -->
  <password-modal v-model="passwordVisible" />
</template>

<script lang="ts" setup>
  import { computed, ref, onMounted } from 'vue';
  import { AppstoreOutlined, ArrowRight } from '@/components/icons';
  import { useUserStore } from '@/store/modules/user';

  import type { EleDropdownInstance } from 'ele-admin-plus/es/ele-app/plus';
  import type { Role } from '@/api/system/role/model';
  import type { TenantAppConfig } from '@/api/bsl/tenant/app/config/model';
  import type { TenantDataScope } from '@/api/bsl/tenant/data/scope/model';

  import { StorageUtil } from '@/utils/lzd-util';

  import PasswordModal from './password-modal.vue';

  const userStore = useUserStore();
  /** 是否显示修改密码弹窗 */
  const passwordVisible = ref(false);
  const roleDropdownRef = ref<EleDropdownInstance>();
  const open = ref(false);

  onMounted(() => {
    if (switchRoles.value.length > 0) {
      let loginCount = parseInt(
        localStorage.getItem('lzd-hide-tour') || '0',
        10
      );
      if (loginCount == 0) {
        localStorage.setItem('lzd-hide-tour', '1');
      }
      open.value = loginCount < 1;
    }
  });

  /** 当前租户应用 */
  const appConfigs = computed(() => userStore.currAppConfigs ?? []);
  /** 当前用户应用 */
  const currAppConfig = computed(
    () =>
      userStore.currAppConfig ?? {
        seq: 1,
        appId: 'GP',
        appName: '毕业设计管理'
      }
  );
  const switchAppConfigs = computed(() => {
    return appConfigs.value
      .filter((v) => currAppConfig.value.appId !== v.appId)
      .map((v) => {
        return {
          title: `${v.appName}`,
          command: v,
          icon: ArrowRight
        };
      });
  });
  /** 当前应用下拉点击 */
  const onAppConfigDropClick = (data: TenantAppConfig) => {
    StorageUtil.set('currAppName', data.appName);
    return location.replace('/?t=' + Date.now());
  };

  /** 当前用户角色 */
  const roles = computed(() => userStore.currRoles ?? []);
  /** 当前角色 */
  const currRole = computed<Partial<Role>>(() => userStore.currRole ?? {});
  /** 多角色切换 */
  const switchRoles = computed(() => {
    return roles.value
      .filter((v) => currRole.value.roleCode !== v.roleCode)
      .map((v) => {
        return {
          title: `${v.roleName}`,
          command: v,
          icon: ArrowRight
        };
      });
  });
  /** 角色下拉点击 */
  const onRoleDropClick = (data: Role) => {
    StorageUtil.set('currRoleSeq', data.seq);
    StorageUtil.set('currRoleCode', data.roleCode);
    return location.replace('/?t=' + Date.now());
  };

  /** 数据范围 */
  const currDataScope = computed(() => userStore.currDataScope);
  const switchDataScopes = computed(() =>
    (userStore.currDataScopes ?? [])
      .filter((v) => v.dataScopeId != currDataScope.value?.dataScopeId)
      .map((v) => {
        return {
          title: `${v.dataScopeId}`,
          command: v,
          icon: ArrowRight,
          slot: 'dataScope'
        };
      })
  );
  /** 数据范围下拉点击 */
  const onDataScopeDropClick = (data: TenantDataScope) => {
    StorageUtil.set('currDataScopeId', data.dataScopeId);
    StorageUtil.remove('currRoleSeq');
    StorageUtil.remove('currRoleCode');
    return location.replace('/?t=' + Date.now());
  };
</script>

<style lang="scss" scoped>
  .header-avatar {
    display: flex;
    align-items: center;
    position: relative;
    height: 100%;
    outline: none;
  }
</style>
